import { useRowSelect } from '@table-library/react-table-library/select';
import { Action, State } from '@table-library/react-table-library/types/common';
import { Button, TableItem } from 'components/atoms';
import { FormInput } from 'components/molecules';
import Popup from 'components/molecules/popup';
import { FooterTable, Table, TopBar } from 'components/organisms';
import { TopbarPage } from 'components/templates';
import { usePermissionGuard } from 'hooks/usePermissionGuard';
import { useTableColumnsVisibility } from 'hooks/useTableColumnsVisibility';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  useGetUnpaidCosts,
  useGetUnpaidCostsStatistics,
} from 'services/cash-flow/cost';
import { useGetLabelLightList } from 'services/labels/light';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { formatCurrency } from 'utils/global/currency.utils';
import { getMaxFormattedLength } from 'utils/global/getMaxFormattedLength.utils';
import { object, string } from 'yup';

enum COLUMNS {
  name,
  label,
  net,
  gross,
  tax,
  amountToRepayment,
}

const Cost = () => {
  usePermissionGuard('SettlementCostPermissionsLIST**', '/', true, 'statements');

  const { t } = useTranslation();
  const navigate = useNavigate();

  const columnsVisibility = useTableColumnsVisibility('Cost', COLUMNS);

  const costs = useGetUnpaidCosts();
  const statistics = useGetUnpaidCostsStatistics();

  const data = {
    nodes:
      costs.data?.pages.flat().map(data => ({
        ...data,
        id: `/label/${data.labelId}/cost/${data.id}/repayments`,
      })) ?? [],
  };

  const onSelectChange = (action: Action, state: State) => {
    const id: string = state.id ?? state.ids[state.ids.length - 1];
    navigate(id, { state: true });
  };

  const select = useRowSelect(data, {
    onChange: onSelectChange,
  });

  const sumAmountToRepayLength = formatCurrency(
    statistics?.data?.sumAmountToRepay
  ).length;

  const amountNetLength = getMaxFormattedLength(data?.nodes ?? [], e => e?.net);
  const amountGrossLength = getMaxFormattedLength(
    data?.nodes ?? [],
    e => e?.gross
  );

  const taxLength = getMaxFormattedLength(data?.nodes ?? [], e => e?.tax, {
    suffix: '%',
    minimumFractionDigits: 0,
  });

  const labelsLightLits = useGetLabelLightList();

  return (
    <TopbarPage
      topbar={
        <TopBar
          title={t(`unpaid.cost`)}
          path=''
          hideBackButton
          topbarContent={
            <div className='flex gap-2'>
              <Table.CustomColumnsButton
                columns={COLUMNS}
                isClear
                visibility={columnsVisibility}
                customI18next=''
                i18nextPrefix={'input.label'}
              />
              <Popup.Form
                header={t('button.create-what', { what: t('cost.what') })}
                initialValues={{ labelId: '' }}
                validationSchema={object().shape({
                  labelId: string().required(() => t('formik-error.required')),
                })}
                trigger={
                  <Button variant={BUTTON_VARIANT.submit}>
                    <Button.Text text={t('button.create')} />
                  </Button>
                }
                submitButton={
                  <Button type='submit' variant={BUTTON_VARIANT.submit}>
                    <Button.Text text={t('button.go-to-form')} />
                  </Button>
                }
                onSubmit={async values =>
                  navigate(`/label/${values.labelId}/cost/create/file`, {
                    state: true,
                  })
                }>
                <FormInput.Select
                  name='labelId'
                  label={t('input.label.label')}
                  options={labelsLightLits.data?.map(label => ({
                    value: label.id,
                    label: label.labelName,
                  }))}
                />
              </Popup.Form>
            </div>
          }
        />
      }>
      <FooterTable
        id={columnsVisibility.tableId}
        loading={
          costs.isLoading || costs.isFetchingNextPage || statistics.isLoading
        }
        onFullyScroll={costs.fetchNextPage}
        select={select}
        data={data}
        displayMode
        columns={[
          {
            label: t('input.label.name'),
            renderCell: ({ costName }) => (
              <TableItem>
                <TableItem.Text text={costName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.name),
            footer: t('table.total'),
          },
          {
            label: t('input.label.label'),
            renderCell: ({ labelName }) => (
              <TableItem>
                <TableItem.Text text={labelName} />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.label),
          },
          {
            label: t('input.label.net'),
            renderCell: ({ net }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(net, { length: amountNetLength })}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.net),
          },
          {
            label: t('input.label.gross'),
            renderCell: ({ gross }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(gross, { length: amountGrossLength })}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.gross),
          },
          {
            label: t('input.label.tax'),
            renderCell: ({ tax }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(tax, {
                    length: taxLength,
                    suffix: '%',
                    minimumFractionDigits: 0,
                  })}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.tax),
          },
          {
            label: t('input.label.amountToRepayment'),
            renderCell: ({ amountToRepayment }) => (
              <TableItem>
                <TableItem.Currency
                  text={formatCurrency(amountToRepayment, {
                    length: sumAmountToRepayLength,
                  })}
                />
              </TableItem>
            ),
            resize: true,
            hide: columnsVisibility.hide(COLUMNS.amountToRepayment),
            footer: formatCurrency(statistics.data?.sumAmountToRepay),
          },
        ]}
      />
    </TopbarPage>
  );
};

export default Cost;
